/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
    Divider,
    Row,
    Col,
    Form,
    DatePicker,
    Select,
    Input,
    Tooltip,
    Typography,
} from "antd";
import Button from "@mui/material/Button";
import LoadingButton from '@mui/lab/LoadingButton';
import { CheckCircleOutlined } from '@ant-design/icons';
import { v4 as uuidv4 } from 'uuid';
import FormItemAddress from "../../../formItems/address";
import FormItemDollarAmount from "../../../formItems/dollarAmount";
import FormItemAccountNumber from "../../../formItems/accountNumber";
import FormItemRoutingNumber from "../../../formItems/routingNumber";
import { backendGetRequest, backendPostWithoutRefresh, backendPostReturnPromise } from "../../../../helpers/callBackend";
import GenericModal from "../../../buttons/genericModal";
import moment from 'moment';
import GenerateCode from "../../../formItems/generateCode";
import { detokenizeAccountDetails } from "../../../../helpers/getDetokenizeAccountDetails";
import { errorMessage } from "../../../../helpers/errorMessageFunc";
import { amountStrtoFloat, toPrice } from "../../../../helpers/stringFormat";
import RecipientCanOptOutMessage from "../recipient/RecipientCanOptOutMessage";
import { Typography as MuiTypography } from '@mui/material'
import checkFlag from '../../../../helpers/featureFlags'
import { useUcs } from "../../../../helpers/contexts/UserCompanySettingsContext";
import SelectPaymentMethodV2 from "../../../formItems/select/SelectPaymentMethodV2";
import Stack from '@mui/material/Stack'

const { TextArea } = Input;
const { Text } = Typography;
const { Option } = Select;


function InitiateExternal(props) {
    const [state, setState] = useState({
        current: 0,
        formInputs: {
            paymentType: "",
            scheduledDate: "",
        },
        dataPastRecipients: [],
        pastRecipient: props.recipientId ? props.recipientId : "",
        verificationToken: '',
        verificationCode: '',
        idempotencyKey: uuidv4(),
        recipientsPageCountExpected: Infinity,
        recipientsPageCountReceived: 0,
        loading: true,
        visible: false,
        inFlight: false,
        clearFormInputs: false,
    })
    const [form] = Form.useForm();
    const formRef = useRef();
    formRef.current = form;
    const sortedPastRecipients = useMemo(() => {
        return [...state.dataPastRecipients].sort((a, b) => {
            const aName = a?.retrieved_values?.name || "";
            const bName = b?.retrieved_values?.name || "";
            return aName.localeCompare(bName, undefined, { sensitivity: 'base' });
        });
    }, [state.dataPastRecipients]);

    const ucsContext = useUcs()

    const isSoftwareCustomer = JSON.parse(localStorage.getItem("isSoftwareCustomer"))

    let queryParams = {}
    if (props.bankAccountSource !== "UN") {
        queryParams = { fundingSources: true }
    }

    const next = () => {
        setState((prevState) => ({ ...prevState, current: prevState.current + 1 }));
    };

    const prev = () => {
        setState((prevState) => ({ ...prevState, current: prevState.current - 1 }));
    };

    useEffect(() => {
        setState((prevState) => ({
            ...prevState,
            loading: (state.recipientsPageCountExpected > state.recipientsPageCountReceived)
        }));
    }, [state.recipientsPageCountExpected, state.recipientsPageCountReceived]);

    useEffect(() => {
        backendGetRequest("/api/payments/counterparty/", { params: { pageIndex: 0, ...queryParams } })
            .then((res) => {
                setState((prevState) => ({
                    ...prevState,
                    recipientsPageCountExpected: res?.data?.total_pages,
                }));
                for (let page = 1; page <= res?.data?.total_pages; page++) {
                    backendGetRequest("/api/payments/counterparty/", { params: { pageIndex: page, ...queryParams } })
                        .then((res) => {
                            setState((prevState) => ({
                                ...prevState,
                                dataPastRecipients: [...prevState.dataPastRecipients, ...res?.data?.data],
                                recipientsPageCountReceived: prevState.recipientsPageCountReceived + 1,
                            }));
                        })
                        .catch((error) => {
                            setState((prevState) => ({
                                ...prevState,
                                recipientsPageCountExpected: prevState.recipientsPageCountExpected - 1,
                            }));
                        })
                }
            })
            .catch((error) => {
                console.error(error);
                setState((prevState) => ({
                    ...prevState,
                    recipientsPageCountExpected: 0,
                    recipientsPageCountReceived: 0,
                }));
            });
    }, []);

    const resetFormInputs = () => {
        if (formRef.current) {
            formRef.current.resetFields();
        }
        setState((prevState) => ({
            ...prevState,
            formInputs: {
                amount: "",
                paymentType: "",
                scheduledDate: "",
                description: "",
                name: "",
                email: "",
                routingNumber: "",
                accountType: "",
                vendorType: "",
                accountStreetLine1: "",
                accountStreetLine2: "",
                accountCity: "",
                accountState: "",
                accountZipCode: "",
                bankName: "",
                bankStreetLine1: "",
                bankStreetLine2: "",
                bankState: "",
                bankZipCode: "",
                accountNumber: "",
            },
            pastRecipient: "",
        }));
    }

    const fetchAccountDetails = useCallback(async () => {
        return await detokenizeAccountDetails(state.pastRecipient ? state.pastRecipient : "");
    }, [state.pastRecipient]);


    useEffect(() => {
        if (state.clearFormInputs) {
            resetFormInputs();
            setState((prevState) => ({ ...prevState, clearFormInputs: false }));
        }
    }, [state.clearFormInputs]);

    useEffect(() => {
        const fetchAndSetAccountDetails = async () => {
            let accountDetails = await fetchAccountDetails();
            setState((prevState) => ({
                ...prevState,
                formInputs: {
                    ...prevState.formInputs,
                    routingNumber: accountDetails.routingNumber,
                    accountNumber: accountDetails.accountNumber,
                }
            }));
            formRef.current.setFieldsValue({
                name: state.formInputs.name,
                email: state.formInputs.email,
                vendorType: state.formInputs.vendorType,
                routingNumber: accountDetails.routingNumber,
                accountNumber: accountDetails.accountNumber,
                accountType: state.formInputs.accountType,
                accountStreetLine1: state.formInputs.accountStreetLine1,
                accountStreetLine2: state.formInputs.accountStreetLine2,
                accountCity: state.formInputs.accountCity,
                accountState: state.formInputs.accountState,
                accountZipCode: state.formInputs.accountZipCode,
                bankName: state.formInputs.bankName,
                bankStreetLine1: state.formInputs.bankStreetLine1,
                bankStreetLine2: state.formInputs.bankStreetLine2,
                bankCity: state.formInputs.bankCity,
                bankState: state.formInputs.bankState,
                bankZipCode: state.formInputs.bankZipCode,
            });
        }
        formRef.current.setFieldsValue({
            name: "",
            email: "",
            vendorType: "",
            routingNumber: "",
            accountNumber: "",
            accountType: "",
            accountStreetLine1: "",
            accountStreetLine2: "",
            accountCity: "",
            accountState: "",
            accountZipCode: "",
            bankName: "",
            bankStreetLine1: "",
            bankStreetLine2: "",
            bankCity: "",
            bankState: "",
            bankZipCode: "",
        });
        fetchAndSetAccountDetails()
    }, [fetchAccountDetails, state.pastRecipient]);

    useEffect(() => {
        const fetchAndSetAccountDetails = async () => {
            var data = state.dataPastRecipients.filter(
                (item) => (item.id === props.recipientId) !== false
            )
            let accountDetails = fetchAccountDetails();
            setState((prevState) => ({
                ...prevState,
                formInputs: {
                    ...prevState.formInputs,
                    // amount: props.amount,
                    name: data[0].retrieved_values.name,
                    email: data[0].retrieved_values.email,
                    vendorType: data[0].retrieved_values.vendor_type,
                    routingNumber: accountDetails.routingNumber,
                    accountNumber: accountDetails.accountNumber,
                    accountType: data[0].retrieved_values.account_type,
                    accountStreetLine1: data[0].address.street_line_1,
                    accountStreetLine2: data[0].address.street_line_2,
                    accountCity: data[0].address.city,
                    accountState: data[0].address.state,
                    accountZipCode: data[0].address.zip_code,
                    bankName: data[0].retrieved_values.bank?.name,
                    bankStreetLine1: data[0].bank_address.street_line_1,
                    bankStreetLine2: data[0].bank_address.street_line_2,
                    bankCity: data[0].bank_address.city,
                    bankState: data[0].bank_address.state,
                    bankZipCode: data[0].bank_address.zip_code,
                }
            }));
            formRef.current({
                name: data[0].retrieved_values.name,
                email: data[0].retrieved_values.email,
                vendorType: data[0].retrieved_values.vendor_type,
                routingNumber: accountDetails.routingNumber,
                accountNumber: accountDetails.accountNumber,
                accountType: data[0].retrieved_values.account_type,
                accountStreetLine1: data[0].address.street_line_1,
                accountStreetLine2: data[0].address.street_line_2,
                accountCity: data[0].address.city,
                accountState: data[0].address.state,
                accountZipCode: data[0].address.zip_code,
                bankName: data[0].retrieved_values.bank?.name,
                bankStreetLine1: data[0].bank_address.street_line_1,
                bankStreetLine2: data[0].bank_address.street_line_2,
                bankCity: data[0].bank_address.city,
                bankState: data[0].bank_address.state,
                bankZipCode: data[0].bank_address.zip_code,
            });
        };
        if (state.dataPastRecipients.length !== 0 && props.recipientId !== "" && state.formInputs.bankZipCode === undefined) {
            fetchAndSetAccountDetails();
        }
    }, [fetchAccountDetails, props.recipientId, state, state.dataPastRecipients]);

    const handleVerificatonCode = async (values) => {
        var formInputs = { ...state.formInputs };
        formInputs['verificationCode'] = values.verificationCode;
        setState((prevState) => ({ ...prevState, verificationToken: values.verificationToken, verificationCode: values.verificationCode, formInputs: formInputs }));
    };

    const handleChange = input => e => {
        let newState = { ...state };
        if (input === "paymentType" && e !== "ach") {
            newState["formInputs"]["scheduledDate"] = "";
        }
        if (typeof e === "string" || typeof e === "number") {
            newState["formInputs"][input] = e;
            if (input === "pastRecipient") {
                var data = state.dataPastRecipients.filter(
                    (item) => (item.id === e) !== false
                );
                newState["pastRecipient"] = e;
                newState["formInputs"]['name'] = data[0].retrieved_values.name;
                newState["formInputs"]['email'] = data[0].retrieved_values.email;
                newState["formInputs"]['vendorType'] = data[0].retrieved_values.vendor_type;
                newState["formInputs"]['accountType'] = data[0].retrieved_values.account_type;
                newState["formInputs"]['accountStreetLine1'] = data[0].address.street_line_1;
                newState["formInputs"]['accountStreetLine2'] = data[0].address.street_line_2;
                newState["formInputs"]['accountCity'] = data[0].address.city;
                newState["formInputs"]['accountState'] = data[0].address.state;
                newState["formInputs"]['accountZipCode'] = data[0].address.zip_code;
                newState["formInputs"]['bankName'] = data[0].retrieved_values.bank?.name;
                newState["formInputs"]['bankStreetLine1'] = data[0].bank_address.street_line_1;
                newState["formInputs"]['bankStreetLine2'] = data[0].bank_address.street_line_2;
                newState["formInputs"]['bankCity'] = data[0].bank_address.city;
                newState["formInputs"]['bankState'] = data[0].bank_address.state;
                newState["formInputs"]['bankZipCode'] = data[0].bank_address.zip_code;
            }
        } else if (e.target !== undefined) {
            newState['formInputs'][input] = e.target.value;
        } else {
            newState['formInputs'][input] = e.format("YYYY-MM-DD");
        };
        setState(newState);
    };

    const validateForm = () => {
        // TODO: Code here
        var data = { ...state.formInputs };
        if (!('amount' in data) || data['amount'] === "")
            return [false, 'amount'];
        if (!('paymentType' in data) || data['paymentType'] === "")
            return [false, 'paymentType'];
        if (!('description' in data) || data['description'] === "")
            return [false, 'description'];
        if (!('name' in data) || data['name'] === "")
            return [false, 'name'];
        if (!('routingNumber' in data) || data['routingNumber'] === "")
            return [false, 'routingNumber'];
        if (!('accountNumber' in data) || data['accountNumber'] === "")
            return [false, 'accountNumber'];
        if (!('accountType' in data) || data['accountType'] === "")
            return [false, 'accountType'];
        if (!('vendorType' in data) || data['vendorType'] === "")
            return [false, 'vendorType'];
        if (!('accountStreetLine1' in data) || data['accountStreetLine1'] === "")
            return [false, 'accountStreetLine1'];
        if (!('accountCity' in data) || data['accountCity'] === "")
            return [false, 'accountCity'];
        if (!('accountState' in data) || data['accountState'] === "")
            return [false, 'accountState'];
        if (!('accountZipCode' in data) || data['accountZipCode'] === "")
            return [false, 'accountZipCode'];
        if (!('bankName' in data) || data['bankName'] === "")
            return [false, 'bankName'];
        if (!('bankStreetLine1' in data) || data['bankStreetLine1'] === "")
            return [false, 'bankStreetLine1'];
        if (!('bankCity' in data) || data['bankCity'] === "")
            return [false, 'bankCity'];
        if (!('bankState' in data) || data['bankState'] === "")
            return [false, 'bankState'];
        if (!('bankZipCode' in data) || data['bankZipCode'] === "")
            return [false, 'bankZipCode'];
        if (!('verificationCode' in data) || data['verificationCode'] === "")
            return [false, 'verificationCode'];
        return [true, ""];
    }

    const accountHolderRender = (name, nickname) => {
        if (name === nickname || nickname === undefined || nickname === '') {
            return name
        }
        return `${name} (${nickname})`
    }

    const onFormSubmitModal = async (values) => {
        var validateResponse = validateForm();
        if (state.formInputs.paymentType === 'wire' && state.formInputs.amount < 1000) {
            errorMessage("Wire minimum $1,000. Please use ACH.")
            return
        }

        if (props.availableBalance < amountStrtoFloat(state.formInputs.amount)) {
            errorMessage(
                'Insufficient funds. Amount exceeds the available balance.'
            );
            return;
        }

        if (!ucsContext.loaded) {
            errorMessage('User permissions not yet loaded')
            return
        }

        if (validateResponse[0]) {
            var data = state.formInputs;
            data["bankAccountId"] = props.bankAccountId;
            data["verificationToken"] = state.verificationToken;
            data['idempotencyKey'] = state.idempotencyKey;
            data['counterparty_id'] = state.pastRecipient;
            data["amount"] = amountStrtoFloat(state.formInputs.amount) // send amount as float

            if (props.bankAccountSource !== "UN") {
                // check creation limit for creating payment requests
                if (!ucsContext.checkLimit('create', data.amount)) {
                    errorMessage(
                        `Amount of $${data.amount} exceeds your payment request creation limit of $${ucsContext.createLimit()}`
                    )
                    return
                }

                // payment requests expects a 'otp' param
                data['otp'] = data.verificationCode

                if (!data.otp) {
                    errorMessage(
                        'No code provided, Please input the 6-digit code sent to your phone in order to initiate external payment request'
                    )
                }

                try {
                    setState((prevState) => ({ ...prevState, inFlight: true }));
                    const paymentRequestResp = await backendPostWithoutRefresh(
                        "/api/payments/payment-request/", data
                    )
                    props.updatePaymentRequests(paymentRequestResp.data?.data, "add")
                    setState((prevState) => ({ ...prevState, idempotencyKey: uuidv4(), clearFormInputs: true, inFlight: false }));
                    next();
                    if (formRef.current) {
                        formRef.current.resetFields();
                    }
                } catch (error) {
                    setState((prevState) => ({ ...prevState, idempotencyKey: uuidv4(), clearFormInputs: true, inFlight: false, visible: false, current: 0 }));
                    if (formRef.current) {
                        formRef.current.resetFields();
                    }
                    let errStatus = error.response.status
                    let errMsg = error.response.data.description;
                    if (errMsg === undefined) {
                        errMsg = error.response.data.detail;
                    }
                    errorMessage(`Error [${errStatus}]: ${errMsg}`, 100);
                    setState((prevState) => ({ ...prevState, inFlight: false }));
                }
            } else {
                // check self-approve limit for creating payments from unit accounts
                if (!ucsContext.checkLimit('self-approve', data.amount)) {
                    errorMessage(
                        `Amount of $${data.amount} exceeds your payment self-approval limit of $${ucsContext.selfApproveLimit()}`
                    )
                    return
                }

                if (props.params === "modal") {
                    setState((prevState) => ({ ...prevState, inFlight: true }));
                    backendPostReturnPromise("/api/payments/", data)
                        .then((res) => {
                            props.updateTransactions(res.data, state.formInputs);
                            setState((prevState) => ({ ...prevState, idempotencyKey: uuidv4(), clearFormInputs: true, inFlight: false }));
                            next();
                            if (formRef.current) {
                                formRef.current.resetFields();
                            }
                        }).catch((error) => {
                            setState((prevState) => ({ ...prevState, idempotencyKey: uuidv4(), clearFormInputs: true, inFlight: false, visible: false, current: 0 }));
                            if (formRef.current) {
                                formRef.current.resetFields();
                            }
                            let errStatus = error.response.status
                            let errMsg = error.response.data.description;
                            if (errMsg === undefined) {
                                errMsg = error.response.data.detail;
                            }
                            errorMessage(`Error [${errStatus}]: ${errMsg}`, 100);
                            setState((prevState) => ({ ...prevState, inFlight: false }));
                        });
                }
                else {
                    setState((prevState) => ({ ...prevState, inFlight: true }));
                    backendPostWithoutRefresh("/api/payments/", data, false, false)
                        .then((res) => {
                            if (res.status === 200) {
                                backendPostWithoutRefresh("/api/payables/status/", { payable_id: props.payableId });
                                props.updateTransactions(res.data, state.formInputs);
                                if (formRef.current) {
                                    formRef.current.resetFields();
                                }
                                setState((prevState) => ({ ...prevState, visible: false, idempotencyKey: uuidv4(), clearFormInputs: true, inFlight: false }));
                                if (formRef.current) {
                                    formRef.current.resetFields();
                                }
                            }
                        }).catch((err) => {
                            setState((prevState) => ({ ...prevState, inFlight: false }));
                        })
                }
            }
        }
        else {
            errorMessage("Input " + validateResponse[1].split(/(?=[A-Z])/).join(" ").toUpperCase() + " Field");
        }
    };

    const disabledDate = (currentDate) => {
        if (currentDate) {
            const dateIsBeforeEndOfDay = currentDate.isBefore(moment().endOf("day"));
            const dateIsLessThanThreeDaysAhead = moment(currentDate).isBefore(
                moment()
                    .endOf("day")
                    .add(3, "days")
            );

            return (
                dateIsBeforeEndOfDay ||
                dateIsLessThanThreeDaysAhead
            );
        } else {
            return false;
        }
    };

    const steps = [
        { title: "Payment Details" },
        { title: "Recipient Information" },
        { title: "Recipient Bank" },
        { title: "Review" },
    ];

    const content = [
        <div>
            <Row gutter={16}>
                <Col span={12}>
                    <FormItemDollarAmount
                        ref={formRef}
                        label={"Amount"}
                        name={"amount"}
                        onChange={handleChange('amount')}
                    />
                </Col>
                <Col span={12}>
                    <SelectPaymentMethodV2
                        label="Payment Type"
                        name="paymentType"
                        onChange={handleChange('paymentType')}
                        bankAccountSource={props.bankAccountSource}
                    />
                </Col>

                <Col span={12}>
                    <Form.Item
                        ref={formRef}
                        label="Description"
                        name="description"
                        rules={[{ required: true }]}
                        onChange={handleChange('description')}
                    >
                        <TextArea showCount maxLength={state.formInputs.paymentType === "ach" ? 10 : 50} />
                    </Form.Item>
                </Col>
                {
                    state.formInputs.paymentType === "ach" && <Col span={12}>
                        <Form.Item
                            ref={formRef}
                            label="Addenda"
                            name="addenda"
                            rules={[{ required: false }]}
                            onChange={handleChange('addenda')}
                        >
                            <TextArea showCount maxLength={80} />
                        </Form.Item>
                    </Col>
                }
                <Col span={12}>
                    <Form.Item
                        ref={formRef}
                        label={"Do you want to schedule this payment?"}
                        name={"schedule"}
                        rules={[{ required: false, message: "Please input " + props.name }]}
                        style={{ display: state.formInputs.paymentType === 'ach' && props.bankAccountSource === "UN" ? '' : 'none' }}
                    >
                        <DatePicker
                            style={{ width: "100%" }}
                            disabledDate={disabledDate}
                            onChange={handleChange('scheduledDate')}
                            renderExtraFooter={() => {
                                return (
                                    <Text type="secondary" className="ant-calendar-footer-text">
                                        Scheduled payments must be at least 3 business days in advance.
                                    </Text>
                                );
                            }}
                        />
                    </Form.Item>
                </Col>
            </Row>
        </div>,

        <div style={{ padding: "5%" }}>
            {props.recipientId === "" ?
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            ref={formRef}
                            label="Past Recipients"
                            name="past_recipients">
                            {state.loading ?
                                <Tooltip title="Loading...">
                                    <Select
                                        disabled={true}
                                        placeholder="Select Past recipients"
                                        style={{ width: "100%" }}
                                        dropdownMatchSelectWidth={false}
                                        onChange={handleChange("pastRecipient")}
                                        showSearch
                                        filterOption={(input, option) => {
                                            const recipient = sortedPastRecipients.find(r => r.id === option.value);
                                            if (!recipient) return false;

                                            const fullDisplayName = accountHolderRender(
                                                recipient.retrieved_values?.name || "",
                                                recipient.retrieved_values?.nickname || ""
                                            ).toLowerCase();

                                            const inputLower = input.toLowerCase()

                                            return fullDisplayName.startsWith(inputLower)
                                        }}
                                        allowClear>
                                        {sortedPastRecipients.map((item) => (
                                            <Option value={item.id}>
                                                {accountHolderRender(
                                                    item.retrieved_values.name,
                                                    item.retrieved_values.nickname,
                                                )}
                                            </Option>
                                        ))}
                                    </Select>
                                </Tooltip> :
                                <Select
                                    placeholder="Select Past recipients"
                                    style={{ width: "100%" }}
                                    dropdownMatchSelectWidth={false}
                                    onChange={handleChange("pastRecipient")}
                                    allowClear
                                    showSearch
                                    filterOption={(input, option) => {
                                        const recipient = sortedPastRecipients.find(r => r.id === option.value);
                                        if (!recipient) return false;

                                        const fullDisplayName = accountHolderRender(
                                            recipient.retrieved_values?.name || "",
                                            recipient.retrieved_values?.nickname || ""
                                        ).toLowerCase();

                                        const inputLower = input.toLowerCase()

                                        return fullDisplayName.startsWith(inputLower)
                                    }}
                                >
                                    {sortedPastRecipients.map((item) => (
                                        <Option value={item.id}>
                                            {accountHolderRender(
                                                item.retrieved_values.name,
                                                item.retrieved_values.nickname,
                                            )}
                                        </Option>
                                    ))}
                                </Select>
                            }
                        </Form.Item>
                    </Col>
                    <Col span={6}></Col>
                    <Col span={6}></Col>
                </Row>
                :
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            label="Past Recipients"
                            name="past_recipients">
                            <Select
                                disabled
                                defaultValue={props.recipientId}
                                placeholder="Select Past recipients"
                                style={{ width: "100%" }}
                                dropdownMatchSelectWidth={false}
                                onChange={handleChange("pastRecipient")}
                                showSearch
                                filterOption={(input, option) => {
                                    const recipient = sortedPastRecipients.find(r => r.id === option.value);
                                    if (!recipient) return false;

                                    const fullDisplayName = accountHolderRender(
                                        recipient.retrieved_values?.name || "",
                                        recipient.retrieved_values?.nickname || ""
                                    ).toLowerCase();

                                    const inputLower = input.toLowerCase()

                                    return fullDisplayName.startsWith(inputLower)
                                }}
                                allowClear>
                                {sortedPastRecipients.map((item) => (
                                    <Option value={item.id}>{item.retrieved_values.name}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={6}></Col>
                    <Col span={6}></Col>
                </Row>
            }
            <>
                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item
                            label="Legal Name/Name on Account"
                            name="name"
                            rules={[
                                {
                                    required: true,
                                    message: "Provide name on account.",
                                },
                            ]}
                            onChange={handleChange('name')}
                        >
                            <Input defaultValue={state.pastRecipient !== "" ? state.formInputs.name : ""} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            label="Email"
                            name="email"
                            onChange={handleChange('email')}
                            key={state.pastRecipient !== "" ? state.formInputs.email : ""}
                        >
                            <Input defaultValue={state.pastRecipient !== "" ? state.formInputs.email : ""} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            label="Vendor Type"
                            name="vendorType"
                            rules={[{ required: true }]}
                        >
                            <Select
                                placeholder={state.pastRecipient !== "" ? state.formInputs.vendorType : "Select your Vendor Type"}
                                style={{ width: "100%" }}
                                dropdownMatchSelectWidth={false}
                                onChange={handleChange('vendorType')}
                                allowClear
                                initialValue={state.pastRecipient !== "" ? state.formInputs.vendorType : ""}
                            >
                                <Option value="business">Business</Option>
                                <Option value="personal">Personal</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={8}>
                        <FormItemRoutingNumber
                            label="Routing Number"
                            name="routingNumber"
                            onChange={handleChange('routingNumber')}
                            initialValue={state.pastRecipient !== "" ? state.formInputs.routingNumber : ""}
                        />
                    </Col>
                    <Col span={8}>
                        <FormItemAccountNumber
                            label="Account Number"
                            name="accountNumber"
                            onChange={handleChange('accountNumber')}
                            initialValue={state.pastRecipient !== "" ? state.formInputs.accountNumber : ""}
                        />
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            label="Account Type"
                            name="accountType"
                            rules={[{ required: true }]}
                        >
                            <Select
                                placeholder="Select your Account Type"
                                style={{ width: "100%" }}
                                onChange={handleChange('accountType')}
                                allowClear
                                dropdownMatchSelectWidth={false}
                                initialValue={state.pastRecipient !== "" ? state.formInputs.accountType : ""}
                            >
                                <Option value="checking">Checking</Option>
                                <Option value="savings">Savings</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>
            </>
            <h4>Account Holder Address</h4>
            <Divider style={{ marginTop: "5px" }} />
            <FormItemAddress
                ref={formRef}
                streetLine1={'accountStreetLine1'}
                streetLine2={'accountStreetLine2'}
                city={'accountCity'}
                state={'accountState'}
                zipCode={'accountZipCode'}
                onChange={handleChange}
                streetLine1InitialValue={state.pastRecipient !== "" ? state.formInputs.accountStreetLine1 : ""}
                streetLine2InitialValue={state.pastRecipient !== "" ? state.formInputs.accountStreetLine2 : ""}
                cityInitialValue={state.pastRecipient !== "" ? state.formInputs.accountCity : ""}
                stateInitialValue={state.pastRecipient !== "" ? state.formInputs.accountState : ""}
                zipCodeInitialValue={state.pastRecipient !== "" ? state.formInputs.accountZipCode : ""}
                formType={"sendfunds"}
            />
        </div>,

        <div style={{ padding: "5%" }}>
            <Row gutter={16}>
                <Col span={24}>
                    <Form.Item
                        label="Bank Name"
                        name="bankName"
                        rules={[{ required: true }]}
                        onChange={handleChange('bankName')}
                    >
                        <Input defaultValue={state.pastRecipient !== "" ? state.formInputs.bankName : ""} />
                    </Form.Item>
                </Col>
                <Col span={24}>
                    <FormItemAddress
                        ref={formRef}
                        streetLine1={'bankStreetLine1'}
                        streetLine2={'bankStreetLine2'}
                        city={'bankCity'}
                        state={'bankState'}
                        zipCode={'bankZipCode'}
                        onChange={handleChange}
                        streetLine1InitialValue={state.pastRecipient !== "" ? state.formInputs.bankStreetLine1 : ""}
                        streetLine2InitialValue={state.pastRecipient !== "" ? state.formInputs.bankStreetLine2 : ""}
                        cityInitialValue={state.pastRecipient !== "" ? state.formInputs.bankCity : ""}
                        stateInitialValue={state.pastRecipient !== "" ? state.formInputs.bankState : ""}
                        zipCodeInitialValue={state.pastRecipient !== "" ? state.formInputs.bankZipCode : ""}
                        type={"sendfunds"}
                    />
                </Col>
            </Row>
        </div>,

        <div style={{ padding: "5%" }}>
            {checkFlag('paymentsV2') && !state.pastRecipient && isSoftwareCustomer && <RecipientCanOptOutMessage />}
            <p style={{ marginBottom: "20px", fontWeight: 600, fontSize: "16px" }}>Review</p>
            <>
                <Divider orientation="left">Payment Information</Divider>
                <Row gutter={16}>
                    <Col span={10} style={{ textAlign: "left" }}>Amount</Col>
                    <Col span={10}>{toPrice(state.formInputs.amount)}</Col>
                </Row>
                <Row gutter={16}>
                    <Col span={10} style={{ textAlign: "left" }}>Payment Type</Col>
                    <Col span={10}>{state.formInputs.paymentType === "ach" ? state.formInputs.paymentType.toUpperCase() : state.formInputs.paymentType.charAt(0).toUpperCase() + state.formInputs.paymentType.slice(1)}</Col>
                </Row>
                {state.formInputs.scheduledDate.length > 0 &&
                    (
                        <Row gutter={16}>
                            <Col span={10} style={{ textAlign: "left" }}>Scheduled Date</Col>
                            <Col span={10}>{state.formInputs.scheduledDate}</Col>
                        </Row>
                    )}
                <Row gutter={16}>
                    <Col span={10} style={{ textAlign: "left" }}>Description</Col>
                    <Col span={10}>{state.formInputs.description}</Col>
                </Row>
                <Divider orientation="left">Account Holder Information</Divider>
                <Row gutter={16}>
                    <Col span={10} style={{ textAlign: "left" }}>Legal Name</Col>
                    <Col span={10}>{state.formInputs.name}</Col>
                </Row>
                <Row gutter={16}>
                    <Col span={10} style={{ textAlign: "left" }}>Email</Col>
                    <Col span={10}>{state.formInputs.email}</Col>
                </Row>
                <Row gutter={16}>
                    <Col span={10} style={{ textAlign: "left" }}>Routing Number</Col>
                    <Col span={10}>{state.formInputs.routingNumber}</Col>
                </Row>
                <Row gutter={16}>
                    <Col span={10} style={{ textAlign: "left" }}>Account Number</Col>
                    <Col span={10}>{state.formInputs.accountNumber}</Col>
                </Row>
                <Row gutter={16}>
                    <Col span={10} style={{ textAlign: "left" }}>Account Type</Col>
                    <Col span={10}>{state.formInputs.accountType}</Col>
                </Row>
                <Row gutter={16}>
                    <Col span={10} style={{ textAlign: "left" }}>Vendor Type</Col>
                    <Col span={10}>{state.formInputs.vendorType}</Col>
                </Row>
                <Row gutter={16}>
                    <Col span={10} style={{ textAlign: "left" }}>Street Line 1</Col>
                    <Col span={10}>{state.formInputs.accountStreetLine1}</Col>
                </Row>
                <Row gutter={16}>
                    <Col span={10} style={{ textAlign: "left" }}>Street Line 2</Col>
                    <Col span={10}>{state.formInputs.accountStreetLine2}</Col>
                </Row>
                <Row gutter={16}>
                    <Col span={10} style={{ textAlign: "left" }}>City</Col>
                    <Col span={10}>{state.formInputs.accountCity}</Col>
                </Row>
                <Row gutter={16}>
                    <Col span={10} style={{ textAlign: "left" }}>State</Col>
                    <Col span={10}>{state.formInputs.accountState}</Col>
                </Row>
                <Row gutter={16}>
                    <Col span={10} style={{ textAlign: "left" }}>Zip Code</Col>
                    <Col span={10}>{state.formInputs.accountZipCode}</Col>
                </Row>
                <Divider orientation="left">Bank Information</Divider>
                <Row gutter={16}>
                    <Col span={10} style={{ textAlign: "left" }}>Bank Name</Col>
                    <Col span={10}>{state.formInputs.bankName}</Col>
                </Row>
                <Row gutter={16}>
                    <Col span={10} style={{ textAlign: "left" }}>Street Line 1</Col>
                    <Col span={10}>{state.formInputs.bankStreetLine1}</Col>
                </Row>
                <Row gutter={16}>
                    <Col span={10} style={{ textAlign: "left" }}>Street Line 2</Col>
                    <Col span={10}>{state.formInputs.bankStreetLine2}</Col>
                </Row>
                <Row gutter={16}>
                    <Col span={10} style={{ textAlign: "left" }}>City</Col>
                    <Col span={10}>{state.formInputs.bankCity}</Col>
                </Row>
                <Row gutter={16}>
                    <Col span={10} style={{ textAlign: "left" }}>State</Col>
                    <Col span={10}>{state.formInputs.bankState}</Col>
                </Row>
                <Row gutter={16}>
                    <Col span={10} style={{ textAlign: "left" }}>Zip Code</Col>
                    <Col span={10}>{state.formInputs.bankZipCode}</Col>
                </Row>
            </>
            <Divider />
            {(checkFlag('optPayments') || props.bankAccountSource === 'UN') && (
                <GenerateCode
                    bankAccountId={props.bankAccountId}
                    onChange={handleVerificatonCode}
                    bankAccountSource={props.bankAccountSource}
                />
            )}
            {checkFlag('paymentsV2') && props.bankAccountSource != "UN" && (
                <MuiTypography fontStyle={'italic'} color={'text.disabled'}>
                    Payment will not automatically send. Your payment will need to be approved by a teammate before it gets sent.
                </MuiTypography>
            )}
        </div>,

        <Stack spacing={5} alignItems='center'>
            <CheckCircleOutlined style={{ color: 'green', fontSize: '30px' }} />
            {props.bankAccountSource === 'UN' ? (
                <MuiTypography color='green' sx={{ fontSize: '20px', marginTop: '10px' }}>
                    Payment successfully submitted
                </MuiTypography>
            ) : (
                <Stack spacing={1} alignItems={"center"}>
                    <MuiTypography color='green' sx={{ fontSize: '20px', marginTop: '10px' }}>
                        Payment request successfully created
                    </MuiTypography>
                    <MuiTypography fontStyle={'italic'} color={'text.disabled'}>
                        Note: Payment will not send until the request is approved.
                    </MuiTypography>
                </Stack>
            )}
        </Stack>
    ];

    const modalbody = [<div>
        <div className="steps-content">
            <Form
                ref={formRef}
                layout="vertical"
                name="basic"
                onFinish={onFormSubmitModal}
                autoComplete="off"
            >
                {content[state.current]}
                {state.current === 3 && (
                    <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
                        <LoadingButton
                            disableElevation fullWidth size="large" type="submit" variant="contained" color="primary"
                            sx={{ fontSize: 14, textTransform: 'none' }}
                            htmlType="submit"
                            style={{
                                margin: "3px",
                                width: "50%",
                            }}
                            loading={state.inFlight}
                            loadingPosition="start"
                        >
                            Submit
                        </LoadingButton>
                    </Form.Item>
                )}
            </Form>
        </div>
        <div
            className="steps-action"
            style={{ textAlign: "center" }}
        >
            {state.current > 0 && state.current < 4 && (
                <Button
                    disableElevation size="large" type="submit" variant="contained" color="primary"
                    sx={{ fontSize: 14, width: '100px', textTransform: 'none' }} style={{ margin: "0 8px" }} onClick={() => prev()}>
                    Previous
                </Button>
            )}
            {state.current < 3 && (
                <Button
                    disableElevation size="large" type="submit" variant="contained" color="primary"
                    sx={{ fontSize: 14, width: '100px', textTransform: 'none' }} onClick={() => next()}>
                    Next
                </Button>
            )}
        </div>
    </div>];

    return (
        props.params === "modal" ?
            <GenericModal
                size='lg'
                visible={state.visible}
                handleCancelModal={() => {
                    if (formRef.current) {
                        formRef.current.resetFields();
                    };
                    setState((prevState) => ({ ...prevState, visible: false, current: 0, clearFormInputs: true }));
                }}
                showModal={() => setState((prevState) => ({ ...prevState, visible: true }))}
                button={<span>External Payment</span>}
                modalTitle={'Transfer to External Accounts'}
                modalBody={modalbody[0]}
            />
            :
            modalbody[0]

    );
}

export default InitiateExternal;